/**
 * DesktopGuidanceOverlay — the on-screen guidance layer over the live desktop
 * sign-in view (2026-08-20 owner directive).
 *
 * When the sign-in journey hits a step only the human can do (a captcha / bot
 * check, a 2FA code, credential entry), prest0n shows a pop-over OVER the
 * live browser telling the user exactly what to do — and, when it knows where
 * the obstacle is, points at it with an arrow or rings it with a circle. The
 * PRIMARY prest0n brain (the computer-use model plane) composes the words,
 * the position, and the affordance; this component is a DUMB, deterministic
 * renderer of that typed directive — it never invents copy.
 *
 * Positioned absolutely inside DesktopSignInModal's surface container (the
 * same overlay slot the boot/error cards use), so it rides above the noVNC
 * iframe with no portal and no z gymnastics.
 */

import React from 'react';
import panelTheme from '../panelTheme';
import type { OverlayGuide, ObstacleRect, DesktopPageKind } from '../desktopSignIn';

export interface ActiveGuidance {
  guide: OverlayGuide;
  obstacle: ObstacleRect | null;
  kind: DesktopPageKind;
}

const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

const DesktopGuidanceOverlay: React.FC<{ guidance: ActiveGuidance; onDismiss?: () => void }> = ({ guidance, onDismiss }) => {
  const { guide, obstacle } = guidance;
  // The pop-over anchor comes from the composer (percent of the viewport);
  // clamp so it never spills off the surface. maxWidth keeps it readable.
  // yPct is the popover's CENTER (the composer contract in planner.ts) —
  // rendering it as the top edge clipped the last line + dismiss control
  // inside the surface's overflow:hidden on short viewports (2026-08-21
  // review finding).
  const left = clamp(guide.xPct, 6, 94);
  const top = clamp(guide.yPct, 10, 86);

  return (
    <div
      data-testid="desktop-guidance-overlay"
      aria-live="polite"
      style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 5 }}
    >
      {/* Affordance pointing at the obstacle (only when we know where it is). */}
      {obstacle && guide.affordance === 'circle' && (
        <div
          data-testid="desktop-guidance-circle"
          style={{
            position: 'absolute',
            left: `${clamp(obstacle.xPct - obstacle.wPct / 2 - 1.5, 0, 100)}%`,
            top: `${clamp(obstacle.yPct - obstacle.hPct / 2 - 1.5, 0, 100)}%`,
            width: `${clamp(obstacle.wPct + 3, 4, 100)}%`,
            height: `${clamp(obstacle.hPct + 3, 4, 100)}%`,
            border: `3px solid ${panelTheme.gold}`,
            borderRadius: 12,
            boxShadow: `0 0 0 3px ${panelTheme.goldTintStrong}`,
            animation: 'prest0nGuidePulse 1.6s ease-in-out infinite',
          }}
        />
      )}
      {obstacle && guide.affordance === 'arrow' && (
        <div
          data-testid="desktop-guidance-arrow"
          aria-hidden="true"
          style={{
            position: 'absolute',
            left: `${clamp(obstacle.xPct, 2, 98)}%`,
            top: `${clamp(obstacle.yPct - obstacle.hPct / 2 - 5, 0, 96)}%`,
            transform: 'translate(-50%, -100%)',
            fontSize: '1.6rem', color: panelTheme.gold,
            filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.35))',
            animation: 'prest0nGuideBob 1.4s ease-in-out infinite',
          }}
        >
          ↓
        </div>
      )}

      {/* The pop-over itself — prest0n's composed words. */}
      <div
        data-testid="desktop-guidance-popover"
        style={{
          position: 'absolute',
          left: `${left}%`,
          top: `${top}%`,
          transform: 'translate(-50%, -50%)',
          maxWidth: 'min(340px, 74%)',
          pointerEvents: 'auto',
          background: panelTheme.ink,
          color: '#fff',
          padding: '0.7rem 1.4rem 0.7rem 0.9rem',
          borderRadius: 14,
          fontSize: '0.86rem',
          lineHeight: 1.35,
          boxShadow: '0 8px 28px rgba(0,0,0,0.32)',
          border: `1px solid ${panelTheme.gold}`,
        }}
      >
        <span aria-hidden="true" style={{ marginRight: 6 }}>👉</span>
        {guide.text}
        {onDismiss && (
          <button
            onClick={onDismiss}
            aria-label="dismiss guidance"
            data-testid="desktop-guidance-dismiss"
            style={{
              position: 'absolute', top: 2, right: 6,
              background: 'none', border: 'none', cursor: 'pointer',
              color: 'rgba(255,255,255,0.7)', fontSize: '0.9rem', lineHeight: 1, padding: 2,
            }}
          >
            ×
          </button>
        )}
      </div>

      <style>{`
        @keyframes prest0nGuidePulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.45; }
        }
        @keyframes prest0nGuideBob {
          0%, 100% { transform: translate(-50%, -100%); }
          50% { transform: translate(-50%, -120%); }
        }
      `}</style>
    </div>
  );
};

export default DesktopGuidanceOverlay;
